import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus } from "lucide-react";

function Btn({ on, active, title, children, testId }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} title={title} aria-label={title} data-testid={testId}
      className={`rounded-md p-2 transition-colors ${active ? "bg-indigo-100 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>{children}</button>
  );
}

export default function RichEditor({ value, onChange, onUploadImage, testId = "rich-editor" }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true } }),
      Image, Placeholder.configure({ placeholder: "Start writing your article…" })],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { class: "sv-prose min-h-[420px] px-5 py-4 focus:outline-none", "data-testid": `${testId}-content` } },
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);

  if (!editor) return null;
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (!url) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const url = await onUploadImage(file);
    if (url) editor.chain().focus().setImage({ src: url, alt: file.name.replace(/\.[^.]+$/, "") }).run();
  };
  const c = () => editor.chain().focus();

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={testId}>
      <div className="sticky top-0 z-10 flex flex-wrap gap-0.5 border-b border-slate-200 bg-slate-50 p-1.5">
        <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testId={`${testId}-h2`}><Heading2 className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testId={`${testId}-h3`}><Heading3 className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId={`${testId}-bold`}><Bold className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId={`${testId}-italic`}><Italic className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId={`${testId}-ul`}><List className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testId={`${testId}-ol`}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId={`${testId}-quote`}><Quote className="h-4 w-4" /></Btn>
        <Btn on={() => c().setHorizontalRule().run()} title="Divider" testId={`${testId}-hr`}><Minus className="h-4 w-4" /></Btn>
        <Btn on={setLink} active={editor.isActive("link")} title="Link" testId={`${testId}-link`}><Link2 className="h-4 w-4" /></Btn>
        <Btn on={() => fileRef.current?.click()} title="Insert image" testId={`${testId}-image`}><ImagePlus className="h-4 w-4" /></Btn>
        <span className="mx-1 w-px bg-slate-200" />
        <Btn on={() => c().undo().run()} title="Undo" testId={`${testId}-undo`}><Undo2 className="h-4 w-4" /></Btn>
        <Btn on={() => c().redo().run()} title="Redo" testId={`${testId}-redo`}><Redo2 className="h-4 w-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickImage} data-testid={`${testId}-image-input`} />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
